import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  CreditCard, Smartphone, ShieldCheck, WifiOff, Zap, Users, Check, ArrowRight, Clock,
  KeyRound, MapPin, CheckCircle2, ChevronDown, Sparkles, Building2, HardHat, HeartPulse,
  ShoppingBag, Radio, ShieldAlert, Award
} from 'lucide-react';

const steps = [
  {
    step: '01',
    title: 'Pair any standard NFC card or fob',
    desc: 'Pick up standard 13.56 MHz cards or key fobs on Amazon or eBay for roughly 30p each. Tap once against a manager phone to link the card to an employee.'
  },
  {
    step: '02',
    title: 'Set up your entrance point',
    desc: 'Mount a smartphone or tablet at the door in kiosk mode, or place our contactless acrylic desk plaque on your reception counter.'
  },
  {
    step: '03',
    title: 'Staff tap to clock in (1 second)',
    desc: 'Workers tap their card or personal phone as they arrive. An immediate chime and green confirmation screen verify attendance.'
  },
  {
    step: '04',
    title: 'Hours feed directly into payroll',
    desc: 'Start times, breaks, overtime, and statutory UK 12.07% holiday accrual update automatically. Export ready-to-run timesheets straight to Xero or Excel.'
  }
];

const features = [
  {
    icon: Radio,
    title: '1-Second Contactless Tap',
    desc: 'Much faster than paper sheets or typing 4-digit PINs. Staff tap their card and walk through to their shift with zero doorway queue.'
  },
  {
    icon: ShieldCheck,
    title: 'Stop Staff Clocking In for Colleagues',
    desc: 'Every badge carries an encrypted hardware chip ID. Staff cannot forward passwords or punch in for absent coworkers.'
  },
  {
    icon: HardHat,
    title: 'Works Through Gloves & Wet Hands',
    desc: 'Practical for commercial kitchens, bars, construction sites, and clinics where workers wear gloves or have wet hands.'
  },
  {
    icon: CreditCard,
    title: 'Compatible with Standard 30p Cards',
    desc: 'Works with standard ISO 14443 Type A chips (MIFARE Classic, NTAG213, NTAG215), key fobs, stickers, or employee smartphones.'
  },
  {
    icon: WifiOff,
    title: 'Keeps Working if Internet Drops',
    desc: 'If workplace Wi-Fi drops out, the terminal logs scans offline with encrypted timestamps and synchronises as soon as connectivity restores.'
  },
  {
    icon: Zap,
    title: 'Direct Xero & HMRC Timesheet Exports',
    desc: 'Approved hours calculate gross pay, overtime rates, and statutory UK 12.07% holiday accrual with zero manual maths.'
  }
];

const comparisonData = [
  {
    feature: 'Clock-In Speed',
    nfc: '1 second tap',
    biometrics: '3–6 seconds (misreads with wet or dirty fingers)',
    paper: '30–60 seconds (handwriting delays & illegible notes)'
  },
  {
    feature: 'Works with Work Gloves',
    nfc: 'Yes (100% contactless)',
    biometrics: 'No (must take off gloves and dry hands)',
    paper: 'Awkward (shared dirty pens)'
  },
  {
    feature: 'UK GDPR & Privacy Compliance',
    nfc: 'Straightforward (anonymous card chip ID only)',
    biometrics: 'Complex (requires formal Biometric DPIA under UK ICO guidance)',
    paper: 'Poor (timesheets left visible on open counters)'
  },
  {
    feature: 'Hardware & Reader Cost',
    nfc: '£0–£15 (use any phone or tablet + £0.30 cards)',
    biometrics: '£400–£1,200 per wall terminal + installation',
    paper: 'High hidden cost in admin mistakes and manual payroll entry'
  },
  {
    feature: 'Prevents Buddy Punching',
    nfc: 'High (requires physical possession of token)',
    biometrics: 'High',
    paper: 'None (anyone can sign someone else\'s name)'
  }
];

const faqs = [
  {
    q: 'What kind of NFC cards or fobs do I need to buy?',
    a: 'KithHR works with standard ISO 14443 Type A contactless NFC chips, including NTAG213, NTAG215, NTAG216, and MIFARE cards. You can buy blank white cards or plastic key fobs on Amazon or eBay for around 20p to 40p each.'
  },
  {
    q: 'Do I have to buy expensive card reading hardware?',
    a: 'No. Any ordinary Android smartphone or tablet with built-in NFC can act as the kiosk terminal. Staff tap their card against the back of the device, and the shift starts immediately.'
  },
  {
    q: 'Can staff tap their phone instead of carrying a plastic card?',
    a: 'Yes. In Mode B (Contactless Desk Plaque), employees unlock their own smartphone and tap it against the KithHR plaque on the counter or wall. That means you do not even need to leave a tablet at reception.'
  },
  {
    q: 'Is NFC attendance compliant with UK GDPR?',
    a: 'Yes, completely. Unlike fingerprint or facial recognition scanners, KithHR never records or processes biometric bodily data. The system simply maps an anonymous alphanumeric card chip ID to an employee profile in your private company database.'
  },
  {
    q: 'What happens if a worker loses their card?',
    a: 'You can unlink a lost card in one click from the manager dashboard and pair a new replacement card in 5 seconds. The employee can also temporarily clock in using their backup 4-digit PIN.'
  }
];

const NfcCardClockIn: React.FC = () => {
  const navigate = useNavigate();
  const [tapped, setTapped] = useState<boolean>(false);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const triggerTapDemo = () => {
    setTapped(true);
    setTimeout(() => {
      setTapped(false);
    }, 3500);
  };

  const schemaJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'SoftwareApplication',
        name: 'KithHR NFC Card Clock-In System',
        operatingSystem: 'Android, iOS, Web, PWA',
        applicationCategory: 'BusinessApplication',
        url: 'https://kithhr.co.uk/nfc-card-clock-in',
        offers: {
          '@type': 'AggregateOffer',
          lowPrice: '0.00',
          highPrice: '3.49',
          priceCurrency: 'GBP'
        },
        description: 'Instant tap-to-clock-in with contactless NFC cards, key fobs, or phones. Eliminate buddy punching, speed up shift changes, and sync timesheets directly to Xero.'
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map(f => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: f.a
          }
        }))
      }
    ]
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#0F172A] font-sans selection:bg-[#0062D2] selection:text-white">
      <SEO
        title="NFC Card & Fob Clock-In System UK: Instant 1-Second Tap Attendance | KithHR"
        description="Instant tap-to-clock-in with contactless NFC cards, key fobs, or phones. Eliminate buddy punching, speed up shift changes, and sync timesheets directly to Xero."
        canonical="https://kithhr.co.uk/nfc-card-clock-in"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaJsonLd) }}
      />

      {/* HEADER / NAVIGATION (POLISHED WARM CREAM) */}
      <header className="sticky top-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-md border-b border-[#E8E2D8] transition-all">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 font-extrabold text-2xl tracking-tight text-[#0F172A] hover:opacity-90">
            <img src="/logo.png" alt="KithHR Logo" className="w-9 h-9 object-contain" />
            <span>Kith<span className="text-[#0062D2]">HR</span></span>
          </Link>
          <div className="flex items-center gap-6">
            <Link
              to="/tablet-time-clock-kiosk"
              className="text-sm font-semibold text-[#475569] hover:text-[#0062D2] transition-colors hidden sm:block"
            >
              Tablet Kiosk
            </Link>
            <Link
              to="/pricing"
              className="text-sm font-semibold text-[#475569] hover:text-[#0062D2] transition-colors hidden sm:block"
            >
              Pricing
            </Link>
            <button
              onClick={() => navigate('/login')}
              className="bg-[#0062D2] hover:bg-[#004CB5] text-white font-bold text-sm px-5 py-2.5 rounded-full transition-all shadow-sm hover:shadow-md active:scale-95 cursor-pointer"
            >
              Start Free (1-15 Staff)
            </button>
          </div>
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="relative pt-16 pb-20 md:pt-24 md:pb-28 overflow-hidden border-b border-[#E8E2D8]">
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Column: Heading and Context */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-[#005BB5] text-xs font-black tracking-wider uppercase">
                <Radio size={14} className="animate-pulse" /> 1-Second Tap Attendance · Hardware-Free
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#0F172A] leading-[1.12]">
                Instant contactless clock-in for <span className="text-[#0062D2]">UK shift teams</span>.
              </h1>

              <p className="text-lg text-[#475569] max-w-xl mx-auto lg:mx-0 leading-relaxed font-normal">
                Let staff clock in the moment they walk through the door with a 30p contactless card, key fob, or their phone. No passwords, no doorway PIN queues, and no £600 biometric machines.
              </p>

              <div className="pt-2 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                <button
                  onClick={() => navigate('/login')}
                  className="w-full sm:w-auto bg-[#0062D2] hover:bg-[#004CB5] text-white font-bold px-8 py-4 rounded-2xl text-base transition-all shadow-lg shadow-blue-500/20 hover:scale-105 active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
                >
                  Enable NFC Clock-In Free <ArrowRight size={18} />
                </button>
                <Link
                  to="/resources/nfc-desk-plaque-clock-in-guide"
                  className="w-full sm:w-auto bg-white border border-[#E8E2D8] hover:bg-[#F5F1EB] text-[#0F172A] font-semibold px-6 py-4 rounded-2xl text-base transition-all text-center shadow-xs"
                >
                  See Desk Plaque Guide
                </Link>
              </div>

              <div className="pt-4 flex flex-wrap items-center justify-center lg:justify-start gap-6 text-xs text-[#64748B] font-medium">
                <span className="flex items-center gap-1.5"><Check size={14} className="text-emerald-600 stroke-[3]" /> Free forever for up to 15 staff</span>
                <span className="flex items-center gap-1.5"><Check size={14} className="text-emerald-600 stroke-[3]" /> Works with cheap 30p standard cards</span>
                <span className="flex items-center gap-1.5"><Check size={14} className="text-emerald-600 stroke-[3]" /> Works through work gloves &amp; wet hands</span>
              </div>
            </div>

            {/* Right Column: Interactive NFC Terminal Mockup (Cream & White) */}
            <div className="lg:col-span-5 flex justify-center">
              <div className="w-full max-w-sm bg-white border-2 border-[#E8E2D8] rounded-[36px] p-6 shadow-2xl shadow-slate-200/60 relative">
                
                {/* Terminal Header */}
                <div className="text-center pb-4 border-b border-[#E8E2D8]">
                  <span className="text-[10px] uppercase font-black tracking-widest text-[#005BB5] bg-blue-50 px-2.5 py-1 rounded-full border border-blue-200">
                    KithHR NFC Terminal Ready
                  </span>
                  <div className="text-xl font-bold text-[#0F172A] mt-2.5">
                    Hold Badge Near Terminal
                  </div>
                  <p className="text-xs text-[#64748B] mt-0.5">Compatible with standard MIFARE / NTAG cards</p>
                </div>

                {/* Animated Sensor Area */}
                <div className="py-8 flex flex-col items-center justify-center relative">
                  <div className={`w-32 h-32 rounded-full border-2 flex items-center justify-center transition-all duration-500 ${
                    tapped
                      ? 'border-emerald-500 bg-emerald-50 scale-110 shadow-lg shadow-emerald-500/20'
                      : 'border-blue-200 bg-blue-50/70 animate-pulse'
                  }`}>
                    <Radio size={48} className={tapped ? 'text-emerald-600 scale-110 transition-transform' : 'text-[#0062D2]'} />
                  </div>

                  {/* Verification Banner */}
                  {tapped ? (
                    <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-center w-full animate-fade-in shadow-xs">
                      <div className="flex items-center justify-center gap-2 text-emerald-800 font-black text-sm">
                        <CheckCircle2 size={18} className="text-emerald-600" /> BEEP! PUNCH CONFIRMED
                      </div>
                      <div className="text-xs text-[#0F172A] font-bold mt-1">
                        Jordan Miller (Warehouse Lead)
                      </div>
                      <div className="text-[11px] text-[#64748B] font-mono mt-0.5">
                        Clocked In: 08:30:14 AM · Main Site
                      </div>
                    </div>
                  ) : (
                    <div className="mt-6 text-xs text-[#64748B] text-center font-medium">
                      Ready for next employee tap...
                    </div>
                  )}
                </div>

                {/* Interactive Card Button */}
                <div className="pt-3 border-t border-[#E8E2D8]">
                  <button
                    type="button"
                    onClick={triggerTapDemo}
                    disabled={tapped}
                    className="w-full bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold p-3.5 rounded-2xl transition-all shadow-md active:scale-95 flex items-center justify-center gap-3 cursor-pointer"
                  >
                    <CreditCard size={18} className="text-blue-400" />
                    <span>{tapped ? 'Reading Card Chip...' : 'Click to Tap Simulated Staff Badge'}</span>
                  </button>
                  <p className="text-[11px] text-[#64748B] text-center mt-2.5">
                    💡 Click above to test how fast the 1-second chime confirms attendance.
                  </p>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* TWO WAYS TO USE NFC IN KITHHR (CREAM BRANDING) */}
      <section className="py-20 md:py-28 border-b border-[#E8E2D8] bg-[#FAF8F5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs uppercase font-black tracking-widest text-[#005BB5] bg-blue-50 px-3 py-1 rounded-full border border-blue-200">
              Hardware Flexibility
            </span>
            <h2 className="text-3xl sm:text-5xl font-black text-[#0F172A] mt-4 tracking-tight">
              Two ways to run NFC attendance in KithHR
            </h2>
            <p className="text-[#475569] mt-3 text-base sm:text-lg leading-relaxed">
              Whether you want a manager kiosk terminal at the entrance or prefer staff to tap their own phones against a contactless plaque — you choose the setup that fits your business.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            
            {/* Mode A: Staff Tap Cards on Kiosk Phone */}
            <div className="rounded-3xl border border-[#E8E2D8] bg-white p-8 flex flex-col justify-between shadow-xs hover:border-[#0062D2]/50 hover:shadow-md transition-all">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-[#005BB5] text-xs font-black uppercase mb-4 border border-blue-200">
                  <CreditCard size={14} /> Mode A: Manager Kiosk Terminal
                </div>
                <h3 className="text-2xl font-bold text-[#0F172A] mb-3">
                  Staff tap budget badges or key fobs on a kiosk device
                </h3>
                <p className="text-[#475569] text-sm leading-relaxed mb-6">
                  Set up any Android smartphone or tablet by the door. Each worker gets a standard 30p contactless card or key fob. Staff simply tap the back of the kiosk to record their arrival.
                </p>
                <ul className="space-y-3 text-sm text-[#334155] mb-6">
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
                    <span><strong>Best for:</strong> Kitchens, warehouses, construction sites, and cleaning teams.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
                    <span><strong>No phones needed on duty:</strong> Workers leave personal phones in lockers.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
                    <span><strong>Works with gloves:</strong> Instant read through thick work gloves or wet hands.</span>
                  </li>
                </ul>
              </div>
              <div className="pt-4 border-t border-[#E8E2D8] text-xs text-[#64748B] font-medium">
                ✓ Hardware needed: Any smartphone or tablet with NFC + Standard MIFARE / NTAG cards
              </div>
            </div>

            {/* Mode B: Staff Tap Their Own Phone on Fixed Desk Plaque */}
            <div className="rounded-3xl border-2 border-blue-200 bg-white p-8 flex flex-col justify-between shadow-lg shadow-blue-500/5 relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-[#0062D2] text-white text-[10px] uppercase font-black px-3 py-1 rounded-full tracking-wider">
                Most Popular
              </div>
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 text-xs font-black uppercase mb-4 border border-emerald-200">
                  <Smartphone size={14} /> Mode B: Contactless Desk Plaque
                </div>
                <h3 className="text-2xl font-bold text-[#0F172A] mb-3">
                  Staff tap their own phones against the KithHR desk plaque
                </h3>
                <p className="text-[#475569] text-sm leading-relaxed mb-6">
                  Place our compact acrylic plaque or NFC sticker on your front counter, till, or entrance wall. Staff unlock their personal smartphone and tap the sign to confirm arrival.
                </p>

                {/* Plaque Image Feature */}
                <div className="mb-6 rounded-2xl overflow-hidden border border-[#E8E2D8] shadow-sm relative group bg-[#FAF8F5]">
                  <img
                    src="/nfc-desk-plaque.jpg"
                    alt="KithHR Contactless NFC Desk Plaque for Staff Clock In and Clock Out"
                    className="w-full h-56 object-cover object-center group-hover:scale-102 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="p-3 bg-white border-t border-[#E8E2D8] text-xs text-[#475569]">
                    <strong className="text-[#0F172A] block font-bold">KithHR Contactless Acrylic Plaque</strong>
                    Battery-free · Stick to any counter or wall · Physical location proof
                  </div>
                </div>

                <ul className="space-y-3 text-sm text-[#334155] mb-6">
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 size={18} className="text-[#0062D2] shrink-0 mt-0.5" />
                    <span><strong>No tablet needed at reception:</strong> You do not risk leaving an expensive iPad on the counter.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 size={18} className="text-[#0062D2] shrink-0 mt-0.5" />
                    <span><strong>Zero doorway queues:</strong> Multiple staff can tap the sign with their own phones in seconds.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 size={18} className="text-[#0062D2] shrink-0 mt-0.5" />
                    <span><strong>Stops GPS spoofing:</strong> Workers must physically touch the plaque on site; they cannot clock in from bed.</span>
                  </li>
                </ul>
              </div>
              <div className="pt-4 border-t border-[#E8E2D8] text-xs text-[#005BB5] font-semibold">
                ✓ Hardware needed: £0 tablets — just a £2 NFC sticker or acrylic plaque + staff phones
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="py-20 md:py-28 border-b border-[#E8E2D8] bg-[#FDFBF7]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-3xl sm:text-4xl font-black text-[#0F172A]">
              NFC Badges vs Fingerprint Scanners vs Paper Sheets
            </h2>
            <p className="text-[#475569] mt-2 text-base">
              Why UK small businesses are replacing messy paper sheets and invasive fingerprint hardware with contactless tags.
            </p>
          </div>

          <div className="overflow-x-auto rounded-3xl border border-[#E8E2D8] bg-white shadow-xs">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#FAF8F5] border-b border-[#E8E2D8] text-[#475569] text-xs font-bold uppercase tracking-wider">
                <tr>
                  <th className="py-4 px-6">Feature</th>
                  <th className="py-4 px-6 text-[#0062D2] font-black">KithHR NFC Badges</th>
                  <th className="py-4 px-6">Fingerprint Scanners</th>
                  <th className="py-4 px-6">Paper Timesheets</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E2D8]">
                {comparisonData.map((row, idx) => (
                  <tr key={idx} className="hover:bg-[#FAF8F5]/60 transition-colors">
                    <td className="py-4 px-6 font-bold text-[#0F172A]">{row.feature}</td>
                    <td className="py-4 px-6 text-[#005BB5] font-bold bg-blue-50/40">{row.nfc}</td>
                    <td className="py-4 px-6 text-[#64748B]">{row.biometrics}</td>
                    <td className="py-4 px-6 text-[#64748B]">{row.paper}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 4-STEP SETUP FLOW */}
      <section className="py-20 md:py-28 border-b border-[#E8E2D8] bg-[#FAF8F5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-black text-[#0F172A]">
              How to set up contactless attendance in 4 steps
            </h2>
            <p className="text-[#475569] mt-2 text-base">
              No complex networking or electricians needed. Set up in under 5 minutes from your manager smartphone.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map(s => (
              <div key={s.step} className="p-6 rounded-2xl bg-white border border-[#E8E2D8] shadow-xs hover:border-[#0062D2]/40 transition-all">
                <div className="text-3xl font-black text-[#0062D2] font-mono mb-3">{s.step}</div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2">{s.title}</h3>
                <p className="text-xs text-[#64748B] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6 FEATURES SECTION */}
      <section className="py-20 md:py-28 border-b border-[#E8E2D8] bg-[#FDFBF7]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-black text-[#0F172A]">
              Built for busy UK shift environments
            </h2>
            <p className="text-[#475569] mt-2 text-base">
              Everything frontline managers need to record reliable attendance and run payroll without manual admin.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <div key={i} className="p-6 rounded-2xl bg-white border border-[#E8E2D8] shadow-xs hover:border-[#0062D2]/30 hover:shadow-sm transition-all">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#005BB5] flex items-center justify-center mb-4 border border-blue-200">
                  <f.icon size={20} />
                </div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2">{f.title}</h3>
                <p className="text-sm text-[#64748B] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQS SECTION */}
      <section className="py-20 md:py-28 border-b border-[#E8E2D8] bg-[#FAF8F5]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black text-[#0F172A]">NFC attendance questions answered</h2>
            <p className="text-[#475569] mt-2 text-sm">Clear details on cards, hardware, and everyday staff use.</p>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="rounded-2xl border border-[#E8E2D8] bg-white overflow-hidden transition-all shadow-xs"
              >
                <button
                  onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                  className="w-full text-left px-6 py-4 flex items-center justify-between font-bold text-[#0F172A] hover:text-[#0062D2] transition-colors cursor-pointer"
                >
                  <span>{faq.q}</span>
                  <ChevronDown
                    size={18}
                    className={`text-[#64748B] transition-transform duration-300 ${activeFaq === idx ? 'rotate-180 text-[#0062D2]' : ''}`}
                  />
                </button>
                {activeFaq === idx && (
                  <div className="px-6 pb-5 text-sm text-[#475569] border-t border-[#E8E2D8] pt-3 leading-relaxed">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="py-20 md:py-28 bg-[#FDFBF7] text-center">
        <div className="max-w-4xl mx-auto px-6 space-y-6">
          <h2 className="text-3xl sm:text-5xl font-black text-[#0F172A] tracking-tight">
            Stop shift change bottlenecks today
          </h2>
          <p className="text-lg text-[#475569] max-w-xl mx-auto">
            Give your staff the speed of 1-second contactless tap-in. 100% free forever for teams up to 15 employees.
          </p>
          <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/login')}
              className="w-full sm:w-auto bg-[#0062D2] hover:bg-[#004CB5] text-white font-bold px-10 py-4 rounded-2xl text-lg transition-all shadow-lg shadow-blue-500/20 hover:scale-105 active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
            >
              Get Started Free <ArrowRight size={20} />
            </button>
            <Link
              to="/tablet-time-clock-kiosk"
              className="w-full sm:w-auto bg-white border border-[#E8E2D8] hover:bg-[#F5F1EB] text-[#0F172A] font-semibold px-8 py-4 rounded-2xl text-lg transition-all text-center shadow-xs"
            >
              View Tablet Kiosk Details
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#E8E2D8] bg-white py-10 text-xs text-[#64748B]">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="KithHR" className="w-5 h-5 rounded-md" />
            <span className="font-bold text-[#0F172A]">KithHR Workforce Ltd</span>
          </div>
          <div className="flex items-center gap-6 text-[#64748B]">
            <Link to="/tablet-time-clock-kiosk" className="hover:text-[#0062D2]">Tablet Kiosk</Link>
            <Link to="/pin-time-clock" className="hover:text-[#0062D2]">PIN Clock</Link>
            <Link to="/pricing" className="hover:text-[#0062D2]">Pricing</Link>
            <Link to="/terms" className="hover:text-[#0062D2]">Terms</Link>
            <Link to="/privacy-policy" className="hover:text-[#0062D2]">Privacy</Link>
          </div>
          <p>© {new Date().getFullYear()} KithHR. Built for UK Businesses.</p>
        </div>
      </footer>
    </div>
  );
};

export default NfcCardClockIn;
